/**
 * DistrictReport.jsx
 *
 * Guest Report page — pick a single day or a date range, view all in-house
 * guests for that period, and export a formatted Excel workbook for
 * submission to the local district authority.
 *
 * Excel design:
 *   Row 1  — Property name (large, bold, merged across all columns)
 *   Row 2  — Property type / category
 *   Row 3  — Address | Phone | Email | TIN
 *   Row 4  — (blank separator)
 *   Row 5  — "GUEST REPORT" title (merged, bold, underlined)
 *   Row 6  — Report date or period | Generated at (timestamp)
 *   Row 7  — Total guests in-house: N
 *   Row 8  — (blank separator)
 *   Row 9  — Table header row (dark blue background, white text)
 *   Row 10+ — Guest rows (alternating light blue / white)
 *   Last   — Footer with property name and page info
 *
 * Columns: #  |  Room  |  Guest Name  |  Nationality  |  ID Type  |  ID Number  |
 *          Arrival  |  Departure  |  Nights  |  Adults  |  Meal Plan  |
 *          Reservation #  |  Purpose of Visit  |  Corporate
 *
 * ID Type / ID Number are read from the guest's real profile record
 * (Guest.idType / Guest.idNumber) — the report never invents its own copy
 * of that data. Purpose of Visit defaults to "Tourism" on every reservation
 * (set server-side) so it's never blank here; front desk staff can correct
 * individual rows directly in the exported Excel if the actual purpose is
 * different, no need to re-enter it in the system first.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import { fetchDistrictReport } from '../../services/api.js';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = d => {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt) ? d : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const nights = (arr, dep) => {
  if (!arr || !dep) return 0;
  const ms = new Date(dep) - new Date(arr);
  return Math.max(0, Math.round(ms / 86400000));
};

const mealLabel = m => ({
  room_only: 'Room Only', bb: 'B&B', hb: 'Half Board',
  fb: 'Full Board', ai: 'All Inclusive'
}[m] || m || 'Room Only');

const idTypeLabel = t => ({
  passport: 'Passport', national_id: 'National ID',
  drivers_license: "Driver's Licence", other: 'Other',
}[t] || (t ? t.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : ''));

const periodLabel = (from, to) =>
  (!to || to === from) ? fmtDate(from) : `${fmtDate(from)} \u2013 ${fmtDate(to)}`;

// ── Excel builder ─────────────────────────────────────────────────────────────

function buildExcel(guests, from, to, property) {
  const wb = XLSX.utils.book_new();
  const ws = {};

  const isRange = to && to !== from;
  const COLS = 15;   // A-O (added: Included Persons — additional people under the same reservation/room)
  const navy  = '0E2240';
  const teal  = '1A7F8E';
  const ltBlue = 'EBF8FA';
  const white = 'FFFFFF';
  const gold  = 'FFF8E1';

  // Helper — set cell value + style
  const C = (r, c, v, s = {}) => {
    const addr = XLSX.utils.encode_cell({ r, c });
    ws[addr] = { v, t: typeof v === 'number' ? 'n' : 's',
      s: { font: { name: 'Calibri', sz: 11, ...s.font },
           fill: s.bg ? { fgColor: { rgb: s.bg }, patternType: 'solid' } : undefined,
           alignment: { horizontal: s.align || 'left', vertical: 'center', wrapText: true },
           border: s.border ? {
             top:    { style: 'thin', color: { rgb: 'CCCCCC' } },
             bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
             left:   { style: 'thin', color: { rgb: 'CCCCCC' } },
             right:  { style: 'thin', color: { rgb: 'CCCCCC' } },
           } : undefined,
           ...s }
    };
  };

  const merge = (rs, re, cs, ce) => {
    if (!ws['!merges']) ws['!merges'] = [];
    ws['!merges'].push({ s: { r: rs, c: cs }, e: { r: re, c: ce } });
  };

  // ── Header block ──────────────────────────────────────────────────────────
  let row = 0;

  // Row 0 — Property name
  C(row, 0, property?.name || 'Hotel',
    { font: { sz: 20, bold: true, color: { rgb: navy } }, bg: white, align: 'center' });
  merge(row, row, 0, COLS - 1);
  row++;

  // Row 1 — Property type
  C(row, 0, property?.type || '',
    { font: { sz: 11, bold: true, color: { rgb: teal } }, bg: white, align: 'center' });
  merge(row, row, 0, COLS - 1);
  row++;

  // Row 2 — Contact info
  const addr = [property?.addressLine1, property?.city].filter(Boolean).join(', ');
  const contact = [addr && `📍 ${addr}`, property?.phone && `📞 ${property.phone}`,
                   property?.email && `✉ ${property.email}`, property?.tin && `TIN: ${property.tin}`]
    .filter(Boolean).join('    ');
  C(row, 0, contact, { font: { sz: 10, color: { rgb: '64748B' } }, bg: white, align: 'center' });
  merge(row, row, 0, COLS - 1);
  row++;

  // Row 3 — blank
  row++;

  // Row 4 — Report title
  C(row, 0, isRange ? 'GUEST REPORT — DATE RANGE' : 'DAILY GUEST REPORT',
    { font: { sz: 16, bold: true, underline: true, color: { rgb: navy } }, bg: gold, align: 'center' });
  merge(row, row, 0, COLS - 1);
  row++;

  // Row 5 — Report date/period + generated timestamp
  C(row, 0, `Report ${isRange ? 'Period' : 'Date'}: ${periodLabel(from, to)}`,
    { font: { sz: 11, bold: true }, bg: gold });
  merge(row, row, 0, 7);
  C(row, 8, `Generated: ${new Date().toLocaleString('en-GB')}`,
    { font: { sz: 10, color: { rgb: '64748B' } }, bg: gold, align: 'right' });
  merge(row, row, 8, COLS - 1);
  row++;

  // Row 6 — Guest count
  C(row, 0, `Total Guests In-House: ${guests.length}`,
    { font: { sz: 11, bold: true, color: { rgb: teal } }, bg: gold });
  merge(row, row, 0, COLS - 1);
  row++;

  // Row 7 — blank separator
  row++;

  // ── Table header ──────────────────────────────────────────────────────────
  const headers = [
    '#', 'Room', 'Guest Name', 'Nationality', 'ID Type', 'ID Number',
    'Arrival', 'Departure', 'Nights', 'Adults', 'Meal Plan',
    'Confirmation #', 'Purpose of Visit', 'Corporate Account',
    'Included Persons (same room/reservation)',
  ];
  headers.forEach((h, c) => {
    C(row, c, h, {
      font: { sz: 11, bold: true, color: { rgb: white } },
      bg: navy, align: 'center', border: true,
    });
  });
  row++;

  // ── Data rows ─────────────────────────────────────────────────────────────
  guests.forEach((res, i) => {
    const bg = i % 2 === 0 ? white : ltBlue;
    const rowData = [
      i + 1,
      res.roomNumber || '—',
      res.guestName || '—',
      res.guestNationality || '—',
      idTypeLabel(res.guestIdType) || '—',
      res.guestIdNumber || '—',
      fmtDate(res.arrivalDate),
      fmtDate(res.departureDate),
      nights(res.arrivalDate, res.departureDate),
      res.adults || 1,
      mealLabel(res.mealPlan),
      res.confirmationNumber || '—',
      res.purposeOfVisit || 'Tourism',
      res.corporateAccountName || '—',
      res.includedPersons?.length ? res.includedPersons.map(p => p.fullName).join(', ') : '—',
    ];
    rowData.forEach((v, c) => {
      C(row, c, v, { bg, align: c === 0 ? 'center' : 'left', border: true,
        font: { sz: 10, bold: c === 2 } });
    });
    row++;
  });

  // ── Footer ────────────────────────────────────────────────────────────────
  row++;
  C(row, 0,
    `${property?.name || 'Hotel'} — Confidential — ${new Date().toLocaleDateString('en-GB')}`,
    { font: { sz: 9, italic: true, color: { rgb: '94A3B8' } }, align: 'center' });
  merge(row, row, 0, COLS - 1);

  // ── Column widths ─────────────────────────────────────────────────────────
  ws['!cols'] = [
    { wch: 4 },  // #
    { wch: 8 },  // Room
    { wch: 28 }, // Guest Name
    { wch: 14 }, // Nationality
    { wch: 14 }, // ID Type
    { wch: 18 }, // ID Number
    { wch: 14 }, // Arrival
    { wch: 14 }, // Departure
    { wch: 7 },  // Nights
    { wch: 7 },  // Adults
    { wch: 14 }, // Meal Plan
    { wch: 18 }, // Confirmation #
    { wch: 20 }, // Purpose
    { wch: 22 }, // Corporate
    { wch: 32 }, // Included Persons
  ];

  ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: row, c: COLS - 1 } });

  const sheetName = `${isRange ? `${from}_to_${to}` : from} Report`.slice(0, 31);
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  // Summary sheet
  const summaryData = [
    [isRange ? 'GUEST REPORT SUMMARY — DATE RANGE' : 'DAILY GUEST REPORT SUMMARY'],
    [],
    ['Property', property?.name || ''],
    [isRange ? 'Report Period' : 'Report Date', periodLabel(from, to)],
    ['Total Guests', guests.length],
    [],
    ['Nationality Breakdown'],
    ...Object.entries(
      guests.reduce((acc, r) => {
        const nat = r.guestNationality || 'Unknown';
        acc[nat] = (acc[nat] || 0) + 1;
        return acc;
      }, {})
    ).sort((a, b) => b[1] - a[1]).map(([nat, count]) => [nat, count]),
    [],
    ['Purpose of Visit Breakdown'],
    ...Object.entries(
      guests.reduce((acc, r) => {
        const p = r.purposeOfVisit || 'Tourism';
        acc[p] = (acc[p] || 0) + 1;
        return acc;
      }, {})
    ).sort((a, b) => b[1] - a[1]).map(([p, count]) => [p, count]),
  ];
  const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
  wsSummary['!cols'] = [{ wch: 28 }, { wch: 14 }];
  XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

  return wb;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function DistrictReport({ property }) {
  const [mode,     setMode]     = useState('single');   // 'single' | 'range'
  const [dateFrom, setDateFrom] = useState(todayIso());
  const [dateTo,   setDateTo]   = useState(todayIso());
  const [loading,  setLoading]  = useState(false);
  const [data,     setData]     = useState(null);
  const [error,    setError]    = useState('');
  const firstLoad = useRef(false);

  const T = {
    navy: '#0E2240', teal: '#1A7F8E', tealPale: '#EBF8FA',
    white: '#FFFFFF', surface: '#F8FAFC', border: '#E2E8F0',
    slate: '#475569', muted: '#94A3B8', success: '#059669',
    danger: '#DC2626',
  };

  // Keep "to" from drifting before "from" when switching modes / picking dates
  useEffect(() => {
    if (mode === 'single') setDateTo(dateFrom);
    else if (dateTo < dateFrom) setDateTo(dateFrom);
  }, [mode, dateFrom]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!property?.id) return;
    setLoading(true); setError('');
    try {
      const to = mode === 'range' ? dateTo : dateFrom;
      const res = await fetchDistrictReport(property.id, dateFrom, to);
      setData(res);
    } catch (e) {
      setError(e.message || 'Failed to load report');
      setData(null);
    } finally { setLoading(false); }
  }, [property?.id, mode, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const handleExport = () => {
    if (!data?.reservations?.length) return;
    const wb = buildExcel(data.reservations, dateFrom, mode === 'range' ? dateTo : dateFrom, property);
    const filename = mode === 'range'
      ? `District_Guest_Report_${dateFrom}_to_${dateTo}.xlsx`
      : `District_Guest_Report_${dateFrom}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const guests = data?.reservations || [];
  const periodText = mode === 'range' ? periodLabel(dateFrom, dateTo) : fmtDate(dateFrom);

  return (
    <div style={{ maxWidth: 1300, margin: '0 auto', padding: '24px 20px', fontFamily: "'Inter', sans-serif" }}>

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: T.navy, margin: 0 }}>🏛️ District Guest Report</h1>
          <p style={{ fontSize: 12, color: T.muted, margin: '4px 0 0' }}>
            In-house guest list for district authority submission
          </p>
        </div>
      </div>

      {/* Date controls */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 24, flexWrap: 'wrap', gap: 12, background: T.white, border: `1px solid ${T.border}`,
        borderRadius: 12, padding: '14px 16px' }}>

        {/* Single day / Date range toggle */}
        <div style={{ display: 'flex', background: T.surface, borderRadius: 8, padding: 3, gap: 2 }}>
          {[{ id: 'single', label: '📅 Single Day' }, { id: 'range', label: '🗓️ Date Range' }].map(o => (
            <button key={o.id} onClick={() => setMode(o.id)}
              style={{ border: 'none', borderRadius: 6, padding: '8px 14px', fontSize: 12.5, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
                background: mode === o.id ? T.navy : 'transparent',
                color: mode === o.id ? T.white : T.slate }}>
              {o.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            style={{ border: `1px solid ${T.border}`, borderRadius: 8, padding: '9px 13px',
              fontSize: 13, fontFamily: 'inherit', color: T.navy, outline: 'none',
              background: T.white }}
          />
          {mode === 'range' && (
            <>
              <span style={{ color: T.muted, fontSize: 13 }}>to</span>
              <input
                type="date"
                value={dateTo}
                min={dateFrom}
                onChange={e => setDateTo(e.target.value)}
                style={{ border: `1px solid ${T.border}`, borderRadius: 8, padding: '9px 13px',
                  fontSize: 13, fontFamily: 'inherit', color: T.navy, outline: 'none',
                  background: T.white }}
              />
            </>
          )}
          <button onClick={load} disabled={loading}
            style={{ background: T.teal, color: T.white, border: 'none', borderRadius: 8,
              padding: '10px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              fontFamily: 'inherit', opacity: loading ? .6 : 1 }}>
            {loading ? '⏳ Loading…' : '🔍 Load Report'}
          </button>
          <button onClick={handleExport} disabled={!guests.length}
            style={{ background: guests.length ? '#059669' : T.muted, color: T.white,
              border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13,
              fontWeight: 700, cursor: guests.length ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit' }}>
            📥 Export Excel
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 10,
          padding: '12px 16px', color: T.danger, fontSize: 13, marginBottom: 20 }}>
          ⚠️ {error}
        </div>
      )}

      {/* Summary cards */}
      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
          {[
            { label: 'Total In-House', value: guests.length, icon: '👥', color: T.teal },
            { label: 'Local Guests', value: guests.filter(r => r.guestNationality === 'Rwandan' || r.guestNationality === 'RW').length, icon: '🇷🇼', color: T.navy },
            { label: 'Foreign Guests', value: guests.filter(r => r.guestNationality && r.guestNationality !== 'Rwandan' && r.guestNationality !== 'RW').length, icon: '🌍', color: '#7C3AED' },
            { label: mode === 'range' ? 'Report Period' : 'Report Date', value: periodText, icon: '📅', color: '#D97706' },
          ].map(c => (
            <div key={c.label} style={{ background: T.white, border: `1px solid ${T.border}`,
              borderRadius: 12, padding: '16px 18px',
              borderLeft: `4px solid ${c.color}` }}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>{c.icon}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>{c.value}</div>
              <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: .5 }}>{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Guest table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: T.muted, fontSize: 14 }}>
          ⏳ Loading guest list…
        </div>
      ) : guests.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, background: T.surface,
          borderRadius: 14, border: `1px dashed ${T.border}`, color: T.muted }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🏨</div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>No guests in-house on {periodText}</div>
          <div style={{ fontSize: 12, marginTop: 6 }}>All checked-in reservations for this {mode === 'range' ? 'period' : 'date'} will appear here.</div>
        </div>
      ) : (
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>

          {/* Table header */}
          <div style={{ background: T.navy, padding: '12px 18px', display: 'flex',
            alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ color: T.white, fontWeight: 700, fontSize: 14 }}>
              In-House Guests — {periodText}
            </span>
            <span style={{ color: T.tealPale, fontSize: 12 }}>{guests.length} guest(s)</span>
          </div>

          {/* Scrollable table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: T.tealPale }}>
                  {['#','Room','Guest Name','Nationality','ID Type','ID Number','Arrival','Departure',
                    'Nts','Adults','Meal','Conf. #','Purpose','Corporate','Included Persons'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700,
                      color: T.navy, fontSize: 11, whiteSpace: 'nowrap',
                      borderBottom: `2px solid ${T.teal}40` }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {guests.map((res, i) => (
                  <tr key={res.id} style={{ background: i % 2 === 0 ? T.white : '#F8FAFC',
                    borderBottom: `1px solid ${T.border}` }}>
                    <td style={{ padding: '9px 12px', color: T.muted, fontWeight: 700, textAlign: 'center' }}>{i + 1}</td>
                    <td style={{ padding: '9px 12px', fontWeight: 700, color: T.teal }}>
                      {res.roomNumber || <span style={{ color: T.muted }}>—</span>}
                    </td>
                    <td style={{ padding: '9px 12px', fontWeight: 700, color: T.navy }}>
                      {res.guestName || '—'}
                    </td>
                    <td style={{ padding: '9px 12px' }}>{res.guestNationality || '—'}</td>
                    <td style={{ padding: '9px 12px', fontSize: 11 }}>{idTypeLabel(res.guestIdType) || '—'}</td>
                    <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontSize: 11 }}>
                      {res.guestIdNumber || '—'}
                    </td>
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>{fmtDate(res.arrivalDate)}</td>
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>{fmtDate(res.departureDate)}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'center', fontWeight: 700 }}>
                      {nights(res.arrivalDate, res.departureDate)}
                    </td>
                    <td style={{ padding: '9px 12px', textAlign: 'center' }}>{res.adults || 1}</td>
                    <td style={{ padding: '9px 12px', fontSize: 11 }}>{mealLabel(res.mealPlan)}</td>
                    <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontSize: 11, color: T.navy }}>
                      {res.confirmationNumber || '—'}
                    </td>
                    <td style={{ padding: '9px 12px', fontSize: 11, color: T.slate }}>
                      {res.purposeOfVisit || 'Tourism'}
                    </td>
                    <td style={{ padding: '9px 12px', fontSize: 11, color: T.muted }}>
                      {res.corporateAccountName || '—'}
                    </td>
                    <td style={{ padding: '9px 12px', fontSize: 11, color: T.slate, maxWidth: 220 }}>
                      {res.includedPersons?.length
                        ? res.includedPersons.map(p => p.fullName).join(', ')
                        : <span style={{ color: T.muted }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Table footer */}
          <div style={{ background: T.surface, borderTop: `1px solid ${T.border}`,
            padding: '10px 18px', fontSize: 11, color: T.muted }}>
            {guests.length} guest(s) in-house — {periodText} · {property?.name}
          </div>
        </div>
      )}
    </div>
  );
}
